import config from '@config';
import AppTracker from '@business/tracking/AppTracker';
import { getCookie, getRandomInt, setCookie } from '@xFrame4/common/Functions';
import KlaviyoHelper, { KlaviyoOrder, KlaviyoUser } from '@helpers/KlaviyoHelper';
import { TrackerEcommerceData, TrackerEcommerceEventType, TrackerExtraUserData, TrackerProduct } from '@xFrame4/common/Tracker';

export type HtmlCategoryData = {
    name: string,
}

export type HtmlCartData = {
    transactionId?: string,
    discount?: number,
    items: TrackerProduct[]
}

export type HtmlUserData = {
    email: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
    postalCode?: string;
    streetAddress?: string;
    countryCode?: string;
}

export class TrackingHelper
{
    setup()
    {
        console.log('Setting up tracking...');
        
        // create the singleton for the tracker
        AppTracker.instance.on('loaded', () => {
            KlaviyoHelper.instance.on('loaded', () => {
                // setup tracking for the events
                this.setupTrackPageView();
                this.setupTrackSearch();
                this.setupTrackViewCategory();
                this.setupTrackViewProduct();
                this.setupTrackAddToCart();
                this.setupTrackRemoveFromCart();
                this.setupTrackViewCart();
                this.setupTrackInitiateCheckout();
                //this.setupTrackKlaviyoStartedCheckout();
                this.setupTrackAddPaymentInfo();
                this.setupTrackPurchase();
                this.setupTrackingForStandarEcommerceEventsFromUrls();
                this.setupTrackReserveProduct();
                this.setupTrackElementClicks();
                this.setupTrackSpecificPageLoads();

                console.log('Tracking setup complete.');
            });
        });
    }

    /**
     * Setup tracking page views for Google Ads.
     * https://developers.google.com/google-ads/api/docs/dynamic-remarketing/prerequisites
     */
    setupTrackPageView()
    {
        let parameters = {
            ecommerce_page_type: 'other',
            google_business_vertical: 'retail'
        }

        AppTracker.instance.logGoogleEvent('page_view', parameters, config.googleAdsId);
    }

    /**
     * Setup tracking for search.
     */
    setupTrackSearch()
    {
        let searchQuery = this.readSearchDataFromHtml();

        if (searchQuery != null)
        {
            AppTracker.instance.logGoogleEvent('search', {
                search_term: searchQuery
            }, config.googleAdsId);

            KlaviyoHelper.instance.trackSearchedSite({
                searchTerm: searchQuery,
            }, this.getKlaviyoUserFromHtml());
        }
    }

    /**
     * Setup tracking for viewing a category.
     * Tracking is triggered when a category page is viewed (#category-data element exists).
     */
    setupTrackViewCategory()
    {
        let categoryData = this.readCategoryDataFromHtml();
        if (categoryData != null)
        {
            KlaviyoHelper.instance.trackViewedCategory({
                name: categoryData.name,
            }, this.getKlaviyoUserFromHtml());
        }
    }

    /**
     * Setup tracking for viewing the product page.
     * Tracking is triggered when a product page is viewed (The [data-tracker-event=view_item] attribute exists).
     */
    setupTrackViewProduct()
    {
        // Track: view a product
        let productDataElement = document.querySelector('[data-tracker-event=view_item]') as HTMLElement;
        if (productDataElement)
        {
            let product = this.readProductDataFromHtml(productDataElement);
            if (product != null)
            {
                AppTracker.instance.logEcommerceEvent({
                    type: TrackerEcommerceEventType.ViewProducts,
                    value: product.price!,
                    currency: AppTracker.currency,
                    products: [product]
                });
                
                KlaviyoHelper.instance.trackViewedProduct(
                    KlaviyoHelper.klaviyoProductFromTrackerProduct(product),
                    this.getKlaviyoUserFromHtml()
                );
            }
        }
    }

    /**
     * Setup tracking for adding a product to the cart.
     * Event is triggered when the user clicks on the 'add to cart' button.
     * TODO: trigger when the cart is updated on the cart page.
     */
    setupTrackAddToCart()
    {
        let addToCartButtons = document.querySelectorAll('.add-cart');
        let productDataElement = document.querySelector('[data-tracker-event=view_item]') as HTMLElement;

        if (productDataElement != null)
        {
            let trackerProduct = this.readProductDataFromHtml(productDataElement);
            if (trackerProduct != null)
            {
                addToCartButtons.forEach(addToCartButton => 
                {
                    this.setupTrackingForAddToCartButton(addToCartButton as HTMLElement, trackerProduct!);
                });
            }
        }
    }

    /**
     * Setup the tracking for an AddToCart button.
     */
    async setupTrackingForAddToCartButton(button: HTMLElement, trackerProduct: TrackerProduct)
    {
        if (trackerProduct)
        {
            trackerProduct.quantity = 1;

            let trackerEcommerceData: TrackerEcommerceData = {
                type: TrackerEcommerceEventType.AddToCart,
                value: this.calculateProductsValue([trackerProduct]),
                currency: AppTracker.currency,
                products: [trackerProduct]
            }

            button.addEventListener('click', () =>
            {
                AppTracker.instance.logEcommerceEvent(trackerEcommerceData);

                KlaviyoHelper.instance.trackAddedToCart(
                    this.readKlaviyoOrderFromHtml(),
                    KlaviyoHelper.klaviyoProductFromTrackerProduct(trackerProduct),
                    this.getKlaviyoUserFromHtml() 
                );

                if (config.googleAdsAddToCartConversionLabel)
                {
                    const conversionParameters: Record<string, any> = {
                        value: trackerEcommerceData.value,
                        currency: trackerEcommerceData.currency,
                        items: trackerEcommerceData.products.map(product => ({
                            id: product.id,
                            quantity: product.quantity,
                            price: product.price,
                            google_business_vertical: 'retail'
                        }))
                    };

                    const extraUserData = this.getExtraUserDataFromHtml();

                    AppTracker.instance.logGoogleAdsEvent(
                        config.googleAdsAddToCartConversionLabel,
                        conversionParameters,
                        extraUserData
                    );
                }
            });
        }
    }

    /**
     * Setup tracking for removing a product from the cart.
     * Tracking is triggered when the user clicks on the 'remove from cart' button.
     */
    async setupTrackRemoveFromCart()
    {
        let removeFromCartButtons = document.querySelectorAll('.remove-from-cart-button');
        removeFromCartButtons.forEach(removeFromCartButton =>
        {
            let productId = removeFromCartButton.getAttribute('kod');
            if (productId != null)
            {
                this.setupTrackingForRemoveFromCartButton(removeFromCartButton as HTMLElement, productId);
            }
        });
    }

    /**
     * Setup the tracking for a RemoveFromCart button.
     */
    async setupTrackingForRemoveFromCartButton(button: HTMLElement, productId: string)
    {
        // get the quantity
        let quantity = this.getQuantityForRemoveFromCartButton(button);

        if (quantity != 0)
        {
            let htmlCartData = this.readCartDataFromHtml();
            let trackerProduct = htmlCartData.items.find(item => item.id == productId && item.quantity == quantity);
            
            if (trackerProduct != null)
            {
                trackerProduct.quantity = quantity;

                let trackerEcommerceData: TrackerEcommerceData = {
                    type: TrackerEcommerceEventType.RemoveFromCart,
                    value: this.calculateProductsValue([trackerProduct]),
                    currency: AppTracker.currency,
                    products: [trackerProduct]
                }

                button.addEventListener('click', () =>
                {
                    AppTracker.instance.logEcommerceEvent(trackerEcommerceData);
                });
            }
        }
    }

    /**
     * Get the quantity related to a RemoveFromCart button.
     */
    getQuantityForRemoveFromCartButton(button: HTMLElement)
    {
        let quantity = 0;

        let parentElement = button.closest('.table-custom.table-cart');
        let stepper = parentElement != null ? parentElement.querySelector('.stepper') : null;
        if (stepper != null) quantity = parseInt((stepper as HTMLInputElement).value);

        return quantity;
    }

    /**
     * Setup tracking for viewing the cart. 
     * Tracking is triggered when the cart page is viewed (on the cos-de-cumparaturi page the .table-cart element is available)
     */
    setupTrackViewCart()
    {
        if (document.location.href.includes('cos-de-cumparaturi'))
        {
            console.log(this.readCartDataFromHtml());
            let htmlCartData = this.readCartDataFromHtml();
            if (htmlCartData != null)
            {
                let trackerEcommerceData: TrackerEcommerceData = {
                    type: TrackerEcommerceEventType.ViewCart,
                    value: this.calculateProductsValue(htmlCartData.items),
                    currency: AppTracker.currency,
                    products: htmlCartData.items
                }
                AppTracker.instance.logEcommerceEvent(trackerEcommerceData);
            }
        }
    }

    /**
     * Setup tracking for initiate checkout.
     * Tracking is triggered on the first checkout step (URL contains 'detalii-facturare-livrare')
     */
    setupTrackInitiateCheckout()
    {
        if (document.location.href.includes('detalii-facturare-livrare'))
        {
            // track initiate checkout
            this.trackEventFromCartData(
                TrackerEcommerceEventType.InitiateCheckout,
                undefined,
                config.googleAdsBeginCheckoutConversionLabel
            );

            // track with StartedCheckout with Klaviyo when the user leaves the email field
            let emailInput = document.querySelector('[name="email"]');
            if (emailInput != null)
            {
                emailInput.addEventListener('blur', (event: any) =>
                {
                    if (event.target.value)
                    {
                        let email = event.target.value;
                        if (TrackingHelper.isValidEmail(email))
                        {
                            // try to get the user data from the form inputs
                            let name = document.querySelector('[name="fullname"]')?.getAttribute('value');
                            let { firstName, lastName } = TrackingHelper.getFirstAndLastNameFromFullName(name ?? '');
                            let phoneNumber = document.querySelector('[name="telefon"]')?.getAttribute('value');
                            let address = document.querySelector('[name="cim"]')?.getAttribute('value');
                            let region = document.querySelector('#select2-megye-container')?.getAttribute('title');
                            let city = document.querySelector('#select2-varos-container')?.getAttribute('title');
                            let zipCode = document.querySelector('[name="postakod"]')?.getAttribute('value');

                            let klaviyoUser: KlaviyoUser = {
                                email: email,
                                firstName: firstName,
                                lastName: lastName,
                                phoneNumber: phoneNumber ?? '',
                                location: {
                                    address: address ?? '',
                                    region: region ?? '',
                                    city: city ?? '',
                                    zipCode: zipCode ?? '',
                                }
                            };

                            KlaviyoHelper.instance.trackStartedCheckout(
                                this.readKlaviyoOrderFromHtml(),
                                undefined,
                                klaviyoUser
                            );
                        }
                    }
                });
            }
        }
    }

    /**
     * Setup tracking for Klaviyo 'Started Checkout' event.
     * NOT IN USE. This event is tracked when the user leaves the email field on the first checkout step.
     */
    setupTrackKlaviyoStartedCheckout()
    {
        if (document.location.href.includes('metoda-de-plata'))
        {
            KlaviyoHelper.instance.trackStartedCheckout(
                this.readKlaviyoOrderFromHtml(),
                undefined,
                this.getKlaviyoUserFromHtml()
            );
        }
    }

    /**
     * Setup tracking for adding payment info
     * Tracking is triggered on the second checkout step (URL contains 'metoda-de-plata') by clicking the finish order button.
     */
    setupTrackAddPaymentInfo()
    {
        if (document.location.href.includes('metoda-de-plata'))
        {
            let form = document.querySelector('#checkoutstep2form') as HTMLFormElement;
            if (form != null)
            {
                form.addEventListener('submit', async () =>
                {
                    // get payment type
                    let paymentType: string = '';
                    let paymentSelectorElement = document.querySelector('.transpcheck_inners.selected');
                    if (paymentSelectorElement != null)
                    {
                        let paymentMethodId = paymentSelectorElement.getAttribute('id');
                        let paymentMethodName = (paymentSelectorElement.querySelector('.dzib1') as HTMLElement).innerText;
                        paymentType = paymentMethodName;
                    }

                    // track
                    this.trackEventFromCartData(TrackerEcommerceEventType.AddPaymentInfo, {
                        paymentType: paymentType
                    });
                });
            }
        }
    }

    /**
     * Setup tracking for purchase.
     * Tracking is triggered on the following pages:
     *  - comanda-inregistrata-cu-succes
     *  - comanda-si-plata-inregistrata
     */
    async setupTrackPurchase()
    {
        let purchaseConfirmationPages = [
            'comanda-inregistrata-cu-succes',
            //'comanda-si-plata-inregistrata',
            '/platesc', // purchase is tracked when redirecting to the euplatesc payment page
            'plata-cu-tbi'
        ];

        let isPurchasePage = false;
        for (let purchaseConfirmationPage of purchaseConfirmationPages)
        {
            if (document.location.href.includes(purchaseConfirmationPage))
            {
                isPurchasePage = true;
                break;
            }
        }

        if (isPurchasePage)
        {
            let cartData = this.readCartDataFromHtml();
            if (cartData.transactionId != null)
            {
                // Dynamically import the Mart class
                const { Mart } = await import('@business/checkout/Mart');
                
                // get payment method ...
                let mart = await Mart.manager.get({ id: cartData.transactionId});
                console.log(mart);

                if (mart != null)
                {
                    // check the cookie that prevents multiple tracking
                    let cookieName = 'purchase-tracked';
                    let cookieValue = getCookie(cookieName);
                    if (cookieValue != null && cookieValue == mart.id)
                    {
                        console.log('Purchase already tracked. Skipping...');
                        return;
                    }
                    
                    // track
                    this.trackEventFromCartData(TrackerEcommerceEventType.Purchase, {
                        paymentType: mart?.paymentMethod?.translation.title
                    });

                    // track with Google Ads
                    let extraUserData = this.getExtraUserDataFromHtml();
                    AppTracker.instance.logGoogleAdsEvent(config.googleAdsPurchaseConversionLabel, {
                        value: this.calculateProductsValue(cartData.items),
                        currency: AppTracker.currency,
                        // cart data: https://support.google.com/google-ads/answer/9028614#zippy=%2Cexample-of-google-tag-implementation-with-cart-data
                        aw_merchant_id: config.googleMerchantId,
                        aw_feed_country: 'RO',
                        aw_feed_language: 'ro',
                        transaction_id: cartData.transactionId,
                        discount: cartData.discount,
                        items: cartData.items.map(item => ({
                            id: item.id,
                            quantity: item.quantity,
                            price: item.price,
                        }))
                    }, extraUserData);

                    // place a cookie that expires in 365 days so that we do not track the purchase multiple times
                    setCookie(cookieName, mart.id, 365);
                }
            }
        }
    }

    /**
     * Setup tracking for standard ecommerce events. These events are triggered by URLs and page loads.
     */
    async setupTrackingForStandarEcommerceEventsFromUrls()
    {
        let standardEcommerceEvents = [
            { type: TrackerEcommerceEventType.PaymentFailed, urls: ['comanda-nefinalizata-cu-tranzactie-esuata'] },
            { type: TrackerEcommerceEventType.Refund, urls: ['cancelorder.php'] },
            { type: TrackerEcommerceEventType.ChangePaymentMethod, urls: ['schimbare-metoda-de-plata'] },
        ]

        for (let standardEcommerceEvent of standardEcommerceEvents)
        {
            let triggerEvent = false;

            for (let url of standardEcommerceEvent.urls)
            {
                if (document.location.href.includes(url))
                {
                    triggerEvent = true;
                }
            }

            if (triggerEvent)
            {
                let cartData = this.readCartDataFromHtml();
                if (cartData.transactionId != null)
                {
                    // Dynamically import the Mart class
                    const { Mart } = await import('@business/checkout/Mart');
                    
                    // get payment method
                    let mart = await Mart.manager.get({ id: cartData.transactionId});
                    console.log(mart);
                    
                    if (mart != null)
                    {
                        // track
                        this.trackEventFromCartData(standardEcommerceEvent.type, {
                            paymentType: mart?.paymentMethod?.translation.title
                        });
                    }
                }   
            }
        }
    }

    /**
     * Setup tracking for reserving a product. Track both steps (initiation and confirmation).
     */
    setupTrackReserveProduct()
    {
        // track step 1: initiation
        if (document.location.href.includes('rezervati-in-magazin'))
        {
            // get the showroom id from the dropdown
            let showroomId: number | null = null;
            let showroomDropdown = document.querySelector('#id_room');
            if (showroomDropdown != null)
            {
                showroomId = parseInt(showroomDropdown.getAttribute('value') as string);
            }

            // get user info
            let extraUserData: TrackerExtraUserData = {};
            let fullName = document.querySelector('#fullname')?.getAttribute('value');
            if (fullName != null)
            {
                let { firstName, lastName } = TrackingHelper.getFirstAndLastNameFromFullName(fullName);
                extraUserData.firstName = firstName;
                extraUserData.lastName = lastName;
            }

            extraUserData.emails = [document.querySelector('#email')?.getAttribute('value') as string];
            extraUserData.phoneNumbers = [document.querySelector('#telefon')?.getAttribute('value') as string];

            // add tracking
            if (showroomId != null)
            {
                let productDataElement = document.querySelector('[data-tracker-event=reserve_product]') as HTMLElement;
                let product = this.readProductDataFromHtml(productDataElement);
                if (product != null)
                {
                    let trackerEcommerceData: TrackerEcommerceData = {
                        type: TrackerEcommerceEventType.ReserveProduct,
                        value: product.price!,
                        currency: AppTracker.currency,
                        products: [product],
                    }

                    let parameters: any = {
                        ...trackerEcommerceData,
                        showroomId: showroomId,
                        event_category: 'ecommerce',
                    }
                    
                    // add the tracking event for the form submission
                    let form = document.querySelector('form[name="checkoutform2"]');
                    if (form != null)
                    {
                        form.addEventListener('submit', () => {
                            AppTracker.instance.logEvent('reserve_product', parameters, extraUserData);
                        });
                    }
                }
            }
        }

        // track step 2: confirmation
        if (document.location.href.includes('rezervare-reusita'))
        {
            AppTracker.instance.logEvent('reserve_product_confirm')
        }
    }

    /**
     * Setup a tracking event based on the cart data.
     */
    async trackEventFromCartData(type: TrackerEcommerceEventType, additionalData?: any, conversionLabel?: string)
    {
        // cart info
        let cartData = this.readCartDataFromHtml();
        
        // extra user data
        let extraUserData = this.getExtraUserDataFromHtml();

        // track
        if (cartData != null && cartData.items.length > 0)
        {
            let trackerEcommerceData: TrackerEcommerceData = {
                type: type,
                transactionId: cartData.transactionId,
                value: this.calculateProductsValue(cartData.items),
                currency: AppTracker.currency,
                products: cartData.items,
                ...additionalData
            }
            AppTracker.instance.logEcommerceEvent(trackerEcommerceData, extraUserData);

            if (conversionLabel)
            {
                const conversionParameters: Record<string, any> = {
                    value: trackerEcommerceData.value,
                    currency: trackerEcommerceData.currency,
                    items: trackerEcommerceData.products.map(product => ({
                        id: product.id,
                        quantity: product.quantity,
                        price: product.price,
                        google_business_vertical: 'retail'
                    }))
                };

                if (trackerEcommerceData.transactionId != null)
                {
                    conversionParameters.transaction_id = trackerEcommerceData.transactionId;
                }

                AppTracker.instance.logGoogleAdsEvent(
                    conversionLabel,
                    conversionParameters,
                    extraUserData
                );
            }
        }
    }

    /**
     * Calculate the sum of the total value of the products.
     */
    calculateProductsValue(trackerProducts: TrackerProduct[])
    {
        let value = 0;

        for (let trackerProduct of trackerProducts)
        {
            if (trackerProduct.price !== undefined && trackerProduct.quantity !== undefined)
            {
                value += trackerProduct.price * trackerProduct.quantity;
            }
        }

        return value;
    }

    /**
     * Read search query data from the attributes of a special HTML element.
     */
    readSearchDataFromHtml()
    {
        let searchQuery: string | null = null;

        let searchInput = document.querySelector('#search-data');

        if (searchInput != null)
        {
            searchQuery = searchInput.getAttribute('data-search-query');
        }

        return searchQuery;
    }

    /**
     * Read category data from the attributes of a special HTML element.
     */
    readCategoryDataFromHtml()
    {
        let element = document.querySelector('#category-data');

        if (element != null)
        {
            let categoryData: HtmlCategoryData = {
                name: element.getAttribute('data-category-name') as string,
            }

            return categoryData;
        }
        else
        {
            return null;
        }
    }

    /**
     * Read product data from the attributes of a special HTML element.
     * @param element 
     */
    readProductDataFromHtml(element: HTMLElement): TrackerProduct | null
    {
        if (element == null) return null;
        
        let product: TrackerProduct = {
            id: element.getAttribute('data-product-id') as string,
            name: element.getAttribute('data-product-name') as string,
            price: parseFloat(element.getAttribute('data-product-price') as string),
            category: element.getAttribute('data-product-category') as string,
            brand: element.getAttribute('data-product-brand') as string,
            variant: element.getAttribute('data-product-variant') as string,
            url: element.getAttribute('data-product-url') as string,
            imageUrl: element.getAttribute('data-product-image-url') as string,
        };

        return product;
    }

    /**
     * Read the cart data from the HTML code.
     */
    readCartDataFromHtml()
    {
        let htmlCartData: HtmlCartData = { items: [] };

        let cartDataContainer = document.querySelector('#cart-data');
        if (cartDataContainer != null)
        {
            // transaction id
            let transactionIdString = cartDataContainer.getAttribute('data-transaction-id');
            if (transactionIdString != null) htmlCartData.transactionId = transactionIdString;
            
            // items
            let cartDataElements = cartDataContainer.querySelectorAll('div');
            let discount = 0;
            cartDataElements.forEach(cartDataElement =>
            {
                // item
                let cartItem: TrackerProduct = {
                    id: cartDataElement.getAttribute('data-product-id') as string,
                    name: cartDataElement.getAttribute('data-product-name') as string,
                    quantity: parseInt(cartDataElement.getAttribute('data-product-quantity') as string),
                    price: parseFloat(cartDataElement.getAttribute('data-product-price') as string),
                    category: cartDataElement.getAttribute('data-product-category') as string,
                    brand: cartDataElement.getAttribute('data-product-brand') as string,
                    discount: parseFloat(cartDataElement.getAttribute('data-product-discount') as string),
                    variant: cartDataElement.getAttribute('data-product-variant') as string,
                    url: cartDataElement.getAttribute('data-product-url') as string,
                    imageUrl: cartDataElement.getAttribute('data-product-image-url') as string,
                };

                htmlCartData.items.push(cartItem);

                // discount
                let productDiscount = cartDataElement.getAttribute('data-product-discount') ?? '0';
                discount += parseFloat(productDiscount);
            });
            htmlCartData.discount = discount;
        }

        return htmlCartData;
    }

    /**
     * Read a KlaviyoOrder object from the HTML data (if available)
     */
    readKlaviyoOrderFromHtml()
    {
        let htmlCartData = this.readCartDataFromHtml();

        let klaviyoOrder: KlaviyoOrder = {
            itemNames: htmlCartData.items.map(item => item.name),
            products: htmlCartData.items.map(item => KlaviyoHelper.klaviyoProductFromTrackerProduct(item)),
            categories: htmlCartData.items.map(item => item.category ?? ''),
            value: this.calculateProductsValue(htmlCartData.items),
        };

        return klaviyoOrder;
    }

    /**
     * Reads the user data from the HTML code.
     * Returns null if the user data container element is not found.
     */
    readUserDataFromHtml(): HtmlUserData | null
    {
        let userDataContainer = document.querySelector('#user-data');
        if (userDataContainer == null) return null;

        let userData: HtmlUserData = {
            email: userDataContainer.getAttribute('data-email') as string,
            phone: userDataContainer.getAttribute('data-phone') as string,
            firstName: userDataContainer.getAttribute('data-first-name') as string,
            lastName: userDataContainer.getAttribute('data-last-name') as string,
            postalCode: userDataContainer.getAttribute('data-postal-code') as string,
            streetAddress: userDataContainer.getAttribute('data-street-address') as string,
            countryCode: 'RO'
        };

        return userData;
    }

    /**
     * Get an TrackerExtraUserData object from the HTML data.
     */
    getExtraUserDataFromHtml()
    {
        let extraUserData: TrackerExtraUserData = {};
        
        let htmlUserData = this.readUserDataFromHtml();
        if (htmlUserData != null)
        {
            extraUserData.emails = [htmlUserData.email];
            extraUserData.phoneNumbers = htmlUserData.phone !== undefined ? [htmlUserData.phone] : [];
            extraUserData.firstName = htmlUserData.firstName;
            extraUserData.lastName = htmlUserData.lastName;
            extraUserData.postalCode = htmlUserData.postalCode;
            extraUserData.streetAddress = htmlUserData.streetAddress;
            extraUserData.countryCode = htmlUserData.countryCode;
        }

        return extraUserData;
    }

    /**
     * Read a KlaviyoUser object from the HTML data (if available)
     */
    getKlaviyoUserFromHtml()
    {
        let htmlUserData = this.readUserDataFromHtml();
        if (htmlUserData != null)
        {
            let klaviyoUser: KlaviyoUser = {
                email: htmlUserData.email,
                phoneNumber: htmlUserData.phone,
                firstName: htmlUserData.firstName,
                lastName: htmlUserData.lastName,
                location: {
                    address: htmlUserData.streetAddress,
                    zipCode: htmlUserData.postalCode,
                    country: htmlUserData.countryCode,
                }
            };

            return klaviyoUser;
        }

        return undefined;
    }

    /**
     * Setup tracking for clicks on elements.
     */
    setupTrackElementClicks()
    {
        let elements = [
            { selector: '.putchart_prod2_3 addcrtp', eventName: 'button_click_add_to_cart' },
            { selector: '.putchart_prod2_3m addcrtp', eventName: 'button_click_add_to_cart' },
            { selector: '#beginchket3', eventName: 'button_click_view_cart' },
            { selector: '.checkout-methods .button', eventName: 'button_click_go_to_delivery_details' },
            { selector: 'button#checkout_step1_btn', eventName: 'button_click_go_to_select_payment_method' },
            { selector: '#finishmart_btn', eventName: 'button_click_confirm_order' },
            { selector: 'a[href^="mailto:"]', eventName: 'click_contact_email' },
            { selector: 'a[href^="tel:"]', eventName: 'click_contact_phone' },
            { selector: 'a[href*="g.page"]', eventName: 'click_contact_google_maps' },
            { selector: '.contact_magazin.room-id-1', eventName: 'contact_store_otopeni' },
            { selector: '.contact_magazin.room-id-2', eventName: 'contact_store_gheorgheni' },
            { selector: '.video-link', eventName: 'click_play_video' },
            { selector: '.upselltitle', eventName: 'click_upsell_product' },
        ];

        for (let element of elements)
        {
            let htmlElements = document.querySelectorAll(element.selector);
            htmlElements.forEach(htmlElement =>
            {
                htmlElement.addEventListener('click', () =>
                {
                    AppTracker.instance.logEvent(element.eventName);
                });
            });
        }
    }

    /**
     * Setup tracking for some specific page loads.
     */
    setupTrackSpecificPageLoads()
    {
        let pages = [
            { url: 'platesc', eventName: 'page_load_eu_platesc_pending' },
            { url: 'plata-cu-tbi', eventName: 'page_load_tbi_pending' },
            { url: 'comanda-nefinalizata-cu-tranzactie-esuata', eventName: 'page_load_order_not_finalized_payment_failed' },
            { url: 'metoda-de-plata-schimbata-si-tranzactie-esuata', eventName: 'page_load_payment_method_changed_payment_failed' },
            { url: 'comanda-si-plata-inregistrata', eventName: 'page_load_order_and_payment_registered' },
            { url: 'comanda-inregistrata-cu-succes', eventName: 'page_load_order_registered_successfully' },
            { url: 'metoda-de-plata-schimbata-si-tranzactie-reusita', eventName: 'page_load_payment_method_changed_payment_success' },
            { url: 'metoda-de-plata-schimbata-si-tranzactie-in-asteptare', eventName: 'page_load_payment_method_changed_payment_pending' },
        ];

        for (let page of pages)
        {
            if (window.location.href.includes(page.url))
            {
                AppTracker.instance.logEvent(page.eventName);
            }
        }
    }

    /**
     * Check if an email is valid.
     */
    static isValidEmail(email: string): boolean 
    {
        let EMAIL_REGEX: RegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        return EMAIL_REGEX.test(email);
    }

    /**
     * Get the first and last name from a full name.
     */
    static getFirstAndLastNameFromFullName(fullName: string)
    {
        let names = fullName.split(' ');
        let firstName = names[0];
        let lastName = names.length > 1 ? names[1] : '';

        return { firstName: firstName, lastName: lastName };
    }
}

export default TrackingHelper;
